/**
 * Fetches the status of ATMS overall and of every category from the BFF, refreshing on an
 * interval. A failed refresh keeps the last loaded data and reports the error alongside it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AtmsApiClient } from '@amzn/et-console-components';
import { StatusHookState, StatusResponse, toFetchErrorKind } from '../types';
import { REFRESH_INTERVAL_MS } from '../components/constants';

const HISTORY_DAYS = 30;

export function useStatus(): StatusHookState {
  const [state, setState] = useState<StatusHookState>({
    isLoading: true,
    data: null,
    error: null,
    lastUpdated: null,
  });
  const requestIdRef = useRef(0);

  const fetchData = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setState(prev => ({ ...prev, isLoading: prev.data === null, error: null }));

    AtmsApiClient.httpGet(`/api/observability/status?days=${HISTORY_DAYS}`)
      .then((result: StatusResponse) => {
        if (requestId === requestIdRef.current) {
          setState({ isLoading: false, data: result, error: null, lastUpdated: new Date() });
        }
      })
      .catch((err: { status?: number; response?: { status?: number } }) => {
        if (requestId !== requestIdRef.current) return;
        setState(prev => ({ ...prev, isLoading: false, error: toFetchErrorKind(err) }));
      });
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  return state;
}
